// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import {
    CartLineModel,
    CartModel,
    ProductAutocompleteItemModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";

export const enum checkoutOption {
    shippingAddress = "Shipping Address",
    reviewOrder = "Review Order Details",
}

type Componentlist = "Catalog Page" | "Product Carousel";
type OptionType = checkoutOption.shippingAddress | checkoutOption.reviewOrder;
type AnalyticsCart = CartModel | Readonly<Cart>;

const windowExist = typeof window !== "undefined";
const productIndex = 3;

export const push = (data: object) => {
    if (windowExist && Object.prototype.hasOwnProperty.call(window, "dataLayer")) {
        window.dataLayer?.push(data);
    }
};

export const resolveCategory = () => {
    if (windowExist) {
        const resolvedCategory = window.location.pathname
            .slice(1)
            .replace(/-/gu, " ")
            .split("/")
            .filter((category, i) => i !== 0);

        if (resolvedCategory.length === productIndex) {
            resolvedCategory.pop();
        }

        return resolvedCategory.join("/");
    }
};

export const resolveVariant = (title: string) => title.replace(/[sS]*.*( - )/, "");

const formatPrice = (price: number) => price.toFixed(2);

export const mapCartLines = (cartLines: CartLineModel[]): Array<object> => {
    return cartLines?.map((cartLine: CartLineModel) => {
        const price = cartLine.pricing?.unitNetPrice.toFixed(2);
        const brand = cartLine.brand?.name ?? "";

        return {
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            brand,
            ...(price ? { price } : {}),
            quantity: cartLine.qtyOrdered,
            variant: resolveVariant(cartLine.shortDescription),
        };
    });
};

const mapCartLine = (product: ProductModel, productInfo: ProductInfo): object => {
    const priceModel = productInfo.pricing?.unitNetPrice ?? 0;
    const brand = product.brand?.name ?? "";

    return {
        name: product.productTitle,
        id: product.productNumber,
        brand,
        price: formatPrice(priceModel),
        quantity: productInfo.qtyOrdered,
        category: resolveCategory(),
    };
};

const mapAutoCompleteProduct = (product: ProductAutocompleteItemModel): object => {
    return {
        name: product.title,
        id: product.erpNumber,
        brand: product.brandName,
        category: resolveCategory(),
    };
};

const mapProductImpression = (products: ProductModel[] | undefined, pageType: string) =>
    products?.map((product, position) => ({
        name: product.productTitle,
        id: product.productNumber,
        price: product.unitListPrice ? formatPrice(product.unitListPrice) : "",
        category: resolveCategory(),
        list: pageType,
        position: position + 1,
    }));

export const dispatchProductClickEvent = (productContext: ProductContextModel, list: Componentlist) => {
    const { product, productInfo } = productContext;

    push({
        event: "productClick",
        ecommerce: {
            click: {
                actionField: {
                    list,
                },
                products: [mapCartLine(product, productInfo)],
            },
        },
    });
};

export const dispatchProductSearchedClickEvent = (product: ProductAutocompleteItemModel) => {
    push({
        event: "productSearchedClick",
        ecommerce: {
            click: {
                actionField: {
                    list: "Search Input",
                },
                products: [mapAutoCompleteProduct(product)],
            },
        },
    });
};

export const dispatchProductDetailEvent = (productInfo: any, realTimePricingResults?: any) => {
    if (!productInfo) {
        return;
    }

    const product: any = {
        ...productInfo,
        unitListPrice: realTimePricingResults
            ? realTimePricingResults.find((p: { productId: any }) => p.productId === productInfo?.id)?.unitListPrice
            : null,
    };

    push({
        event: "productDetail",
        ecommerce: {
            detail: {
                products: [
                    {
                        name: product.productTitle,
                        id: product.productNumber,
                        price: product.unitListPrice ? formatPrice(product.unitListPrice) : "",
                        brand: product.brand?.name,
                        category: resolveCategory(),
                        variant: (product.childTraitValues && (product.childTraitValues[0].value as string)) ?? "",
                    },
                ],
            },
        },
    });
};

export const dispatchCheckoutEvent = (stepNumber: number, optionType: OptionType, cart: AnalyticsCart) => {
    push({
        event: "checkout",
        ecommerce: {
            checkout: {
                actionField: { step: stepNumber, option: optionType },
                products: mapCartLines(cart.cartLines as CartLineModel[]),
            },
        },
    });
};

export const dispatchAddToCartEvent = (productContext: ProductContextModel, currency: string | undefined) => {
    const { product, productInfo } = productContext;

    push({
        event: "addToCart",
        ecommerce: {
            currencyCode: currency ?? "",
            add: {
                products: [mapCartLine(product, productInfo)],
            },
        },
    });
};

export const dispatchAddToListEvent = (productContext: ProductContextModel, currency: string | undefined) => {
    const { product, productInfo } = productContext;

    push({
        event: "addToList",
        ecommerce: {
            currencyCode: currency ?? "",
            addToList: {
                products: [mapCartLine(product, productInfo)],
            },
        },
    });
};

export const dispatchPurchaseEvent = (cart: CartModel) => {
    push({
        event: "purchase",
        ecommerce: {
            purchase: {
                actionField: {
                    id: cart.orderNumber,
                    affiliation: cart.billTo?.customerName ?? "Online Store",
                    revenue: formatPrice(cart.orderSubTotal),
                    tax: formatPrice(cart.totalTax),
                    shipping: formatPrice(cart.shippingAndHandling),
                },
                products: mapCartLines(cart.cartLines as CartLineModel[]),
            },
        },
    });
};

export const dispatchProductImpressions = (
    products: ProductModel[] | undefined,
    currency: string | undefined,
    pageType: string,
) => {
    if (products?.length === 0) {
        return;
    }

    push({
        event: "productImpression",
        ecommerce: {
            currencyCode: currency ?? "",
            impressions: mapProductImpression(products, pageType),
        },
    });
};

export const dispatchDeleteProduct = (cartLines: CartLineModel[]) => {
    push({
        event: "removeFromCart",
        ecommerce: {
            remove: {
                products: mapCartLines(cartLines as CartLineModel[]),
            },
        },
    });
};
